import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Highlight } from "prism-react-renderer";
import { CopyButton } from "~/components/assistant/CopyButton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { codeTheme } from "~/lib/codeTheme";

// Content can be:
// - string: plain text
// - object: JSON (will be syntax highlighted)
// - InstructionsContent: instructions (plain text) followed by additional args (JSON)
export type InstructionsContent = {
  __kind: "instructions";
  text: string;
  rest: object;
};
type ContentType = string | object | InstructionsContent;

interface DetailFieldProps {
  label?: string;
  content: ContentType;
  maxHeight?: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

/**
 * Check if content is the special instructions format (tagged with __kind).
 */
function isInstructionsContent(content: ContentType): content is InstructionsContent {
  return (
    typeof content === "object" &&
    content !== null &&
    "__kind" in content &&
    (content as InstructionsContent).__kind === "instructions"
  );
}

export function DetailField({
  label,
  content,
  maxHeight = "600px",
  collapsible = false,
  defaultExpanded = true,
}: DetailFieldProps) {
  const { Prism } = usePrismLanguages(["json"]);
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Auto-collapse when defaultExpanded changes from true to false (e.g., when content arrives)
  useEffect(() => {
    if (!defaultExpanded) {
      setExpanded(false);
    }
  }, [defaultExpanded]);

  // Compute display string for copy button
  const getCopyText = (): string => {
    if (isInstructionsContent(content)) {
      return JSON.stringify({ instructions: content.text, ...content.rest }, null, 2);
    }
    return typeof content === "string" ? content : JSON.stringify(content, null, 2);
  };

  const copyText = getCopyText();

  const renderJsonBlock = (obj: object) => {
    const jsonString = JSON.stringify(obj, null, 2);
    return (
      <Highlight theme={codeTheme} code={jsonString} language="json" prism={Prism}>
        {({ style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className="text-xs font-mono px-3 py-2 rounded whitespace-pre-wrap break-words overflow-y-auto"
            style={{ ...style, maxHeight }}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    );
  };

  const renderTextBlock = (text: string) => (
    <pre
      className="text-xs bg-gray-50 text-gray-800 px-3 py-2 rounded whitespace-pre-wrap break-words overflow-y-auto"
      style={{ maxHeight }}
    >
      {text}
    </pre>
  );

  const renderContent = () => {
    // Handle instructions content
    if (isInstructionsContent(content)) {
      return (
        <div className="space-y-2">
          {renderTextBlock(content.text)}
          {renderJsonBlock(content.rest)}
        </div>
      );
    }

    // Handle plain string
    if (typeof content === "string") {
      return renderTextBlock(content);
    }

    // Handle object (JSON)
    return renderJsonBlock(content);
  };

  // No label - just render content directly
  if (!label) {
    return <>{renderContent()}</>;
  }

  if (collapsible) {
    return (
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <div className="flex items-center justify-between mb-1">
          <CollapsibleTrigger className="flex items-center gap-1 hover:text-gray-600 transition-colors cursor-pointer">
            {expanded ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
            <p className="text-xs font-medium">{label}:</p>
          </CollapsibleTrigger>
          <CopyButton text={copyText} variant="light" size="sm" />
        </div>
        <CollapsibleContent animated>{renderContent()}</CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium">{label}:</p>
        <CopyButton text={copyText} variant="light" size="sm" />
      </div>
      {renderContent()}
    </div>
  );
}
